// time-on-task-chrome — active-time recording on the isolated editor surfaces
// (time-on-task, Trello Q1RnFTFi).
//
// Wired UNIFORMLY from main.js, not dispatched by data-surface, and EAGERLY — next to
// wireReadAloud, above the crossOriginIsolated early-return and outside loadProject().then().
// It follows read-aloud-chrome.js exactly, for the same reason: its inputs are the
// SERVER-RENDERED <body> attributes and the surface's own `#*-config` JSON block, so it needs
// neither the project payload nor getEditor(). Nothing forces it late, and an isolation
// failure or a rejected loadProject must not silently disable recording. It is NOT a `SURFACES`
// entry — that map is per-surface-exclusive and this spans seven of them.
//
// It shares ONE recorder with the classroom island: `src/islands/shared/time-on-task.js`. Both
// trees are entries of the same Vite `rollupOptions.input`, so the cross-tree import resolves
// at build time into a shared chunk named `time-on-task` (named explicitly in vite.config.mjs,
// because chunkFileNames routes it into the un-hashed js/shell/ directory).
//
// It never touches editor-core or editor-api, and it changes no postMessage contract.
//
// ── TWO CONTEXT SHAPES, because the surfaces genuinely disagree ────────────────────────────
//   Pattern A — `assignment` and `challenge`: <body data-course-id / data-material-id /
//               data-linked-course-id / data-preview>.
//   Pattern B — the five activity surfaces: a `#<surface>-config` JSON block exposing
//               cfg.courseId / cfg.materialId / cfg.linkedCourseId / cfg.preview.
//   `guided-make` carries body attributes AND a config block; the config wins, because only it
//   carries the class stamp and the preview flag.
//
// ── THE ALLOW-LIST IS LOAD-BEARING ────────────────────────────────────────────────────────
// `lib/views/embed/review.html` and `lib/views/embed/challenge-feedback.html` BOTH emit
// `data-material-id` and NEITHER emits `data-preview`. A "start when there is a material id and
// we are not previewing" test would therefore start the recorder on two TEACHER surfaces and
// quietly record a teacher's marking time as a student's work. Recording is allow-listed BY
// SURFACE, never inferred from the presence of a material id.
//
// Every surface on the allow-list is a trackable activity type by construction, which is what
// keeps this side of the feature consistent with the classroom's own trackability rule: time
// exists only where the reset path can delete it.

import { startTimeOnTask, stopTimeOnTask } from '../../../src/islands/shared/time-on-task.js';

// The nine surfaces a class-enrolled student genuinely works on.
var RECORDING_SURFACES = [
  'assignment',
  'challenge',
  'predict',
  'debug',
  'completion',
  'guided-make',
  'tweak',
  // find-the-bug-activity: a trackable student activity surface with a `#find-the-bug-config`
  // block (courseId / materialId / linkedCourseId / preview), like debug.
  'find-the-bug',
  // compare-activity (Trello m98B2mq7): the same shape — `#compare-config` carries courseId /
  // materialId / linkedCourseId / preview, and the template sets `data-surface="compare"`.
  'compare'
];

function readBodyContext(doc) {
  var body = doc.body;
  if (!body || !body.getAttribute) { return null; }
  return {
    courseId: body.getAttribute('data-course-id') || '',
    materialId: body.getAttribute('data-material-id') || '',
    linkedCourseId: body.getAttribute('data-linked-course-id') || '',
    // A STRING comparison: the attribute is only emitted when previewing, so `!== 'false'`
    // would read an absent attribute as "previewing".
    preview: body.getAttribute('data-preview') === 'true'
  };
}

// The surface's own config block, when it has one. Returns null rather than throwing on
// malformed JSON — a broken config must not take the whole eager wiring down.
function readConfigContext(doc, surface) {
  var el = doc.getElementById(surface + '-config');
  if (!el) { return null; }
  var cfg;
  try {
    cfg = JSON.parse(el.textContent || '{}');
  } catch (e) {
    return null;
  }
  if (!cfg || typeof cfg !== 'object') { return null; }
  return {
    courseId: cfg.courseId ? String(cfg.courseId) : '',
    materialId: cfg.materialId ? String(cfg.materialId) : '',
    linkedCourseId: cfg.linkedCourseId ? String(cfg.linkedCourseId) : '',
    preview: cfg.preview === true
  };
}

/**
 * EAGER entry point, wired from main.js above the crossOriginIsolated early-return and outside
 * loadProject().then(). Null-safe and throw-safe: it runs inside main.js's IIFE, where a throw
 * would skip every later wiring step.
 */
export function wireTimeOnTask(doc) {
  try {
    doc = doc || (typeof document !== 'undefined' ? document : null);
    if (!doc || !doc.body) { return; }

    var surface = doc.body.getAttribute('data-surface') || 'editor';
    if (RECORDING_SURFACES.indexOf(surface) === -1) { return; }

    var body = readBodyContext(doc) || { courseId: '', materialId: '', linkedCourseId: '', preview: false };
    var cfg = readConfigContext(doc, surface);

    // The config block wins on every key it actually carries — it is the only source of the
    // class stamp and the preview flag on the five activity surfaces.
    var ctx = {
      courseId: (cfg && cfg.courseId) || body.courseId,
      materialId: (cfg && cfg.materialId) || body.materialId,
      linkedCourseId: (cfg && cfg.linkedCourseId) || body.linkedCourseId,
      preview: (cfg ? cfg.preview : false) || body.preview
    };

    if (!ctx.courseId || !ctx.materialId) { return; }

    // Belt and braces on preview. The server already refuses (a previewing teacher is not an
    // enrolled student, so nothing resolves), but not starting the beat saves a pointless
    // 60-second POST from every teacher preview.
    if (ctx.preview) { return; }

    var started = startTimeOnTask({
      doc: doc,
      courseId: ctx.courseId,
      materialId: ctx.materialId,
      linkedCourseId: ctx.linkedCourseId
    });
    if (!started) { return; }

    // The teardown must be CALLED, not merely exported. The recorder registers its own
    // `pagehide` flush; this is the listener that also removes its timers, so a bfcache restore
    // never resumes a recorder bound to a page the student has left.
    window.addEventListener('pagehide', function () {
      stopTimeOnTask();
    });
  } catch (e) {
    console.error(e);
  }
}
